import { useController } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { VaultRole } from '@/modules/api/codegen';
import { FormField, Label, SelectInput } from '@/modules/forms';
import { useSubscriptionUpgradeModal } from '@/modules/saas-upgrade';
import { useIsFreeTier } from '@/modules/settings/hooks';
import { IconButton } from '@/modules/ui';
import { useVaults } from '@/modules/vaults';

import { Fields } from '../../../constants';
import type { SecretFormValues } from '../../../schemas';
import * as felaRules from './VaultField.rules';

const name = Fields.VAULT_ID;

// antd refuses `null` as an option value, so "no vault" is an empty id in the list and `null` in the form
const NO_VAULT = '';

export const VaultField = () => {
    const { formatMessage } = useIntl();

    const {
        field: { ref, value, ...field },
    } = useController<SecretFormValues, typeof name>({ name });

    const isFreeTier = useIsFreeTier();
    const { openUpgradeModal } = useSubscriptionUpgradeModal();

    const { vaults, personalVault, teamVaults, query } = useVaults();

    const editorVaults = [...(personalVault ? [personalVault] : []), ...teamVaults].filter(({ userRole }) => userRole === VaultRole.editor);

    // Moving a record out of a vault needs the editor role there too, so a record sitting in a vault the
    // user only views — or cannot see at all — stays where it is, and the field only names the vault.
    const lockedVaultId = typeof value === 'string' && !editorVaults.some(({ id }) => id === value) ? value : null;
    const isLocked = lockedVaultId !== null;

    const options = isLocked
        ? [
              {
                  value: lockedVaultId,
                  label: vaults.find(({ id }) => id === lockedVaultId)?.name ?? formatMessage({ id: `secret.${name}.inaccessible` }),
              },
          ]
        : [
              { value: NO_VAULT, label: formatMessage({ id: `secret.${name}.placeholder` }) },
              ...editorVaults.map(({ id, name: vaultName }) => ({ value: id, label: vaultName })),
          ];

    return (
        <FormField<SecretFormValues>
            name={name}
            htmlFor={name}
            label={
                <Label customStyle={felaRules.label}>
                    <FormattedMessage id={`secret.${name}.label`} />
                    {isFreeTier && (
                        <IconButton
                            type='ghost'
                            icon={{ type: 'premium', color: 'alertsWarningText' }}
                            aria-label={formatMessage({ id: 'saas.upgradeNotice.cta' })}
                            onClick={openUpgradeModal}
                        />
                    )}
                </Label>
            }
        >
            <SelectInput
                {...field}
                // While vaults load the list holds only the spinner, which antd keys as `null`, so `null` keeps showing it in
                // place of a blank label. `undefined` would flip antd to uncontrolled and let it keep showing a value the form has dropped.
                value={value ?? (query.isPending ? null : NO_VAULT)}
                onChange={(next: string) => field.onChange(next === NO_VAULT ? null : next)}
                disabled={isLocked || isFreeTier || editorVaults.length === 0}
                loading={query.isPending}
                id={name}
                placeholder={formatMessage({ id: `secret.${name}.placeholder` })}
                options={options}
                // virtual mode renders the clickable options without a role, mirroring only the active one into a hidden listbox
                virtual={false}
            />
        </FormField>
    );
};
